import {
  RIVER_MOUTH_S_METERS,
  inboundRouteTangent,
  routeLocation,
  type RiverPoint,
} from "../../shared/river-model";
import type { PublicVesselSummary } from "../../shared/contracts";
import {
  vesselHeading,
  vesselTravelSign,
  type HeadingVessel,
  type TravelSign,
} from "../vessel-heading";

const METERS_PER_SECOND_PER_KNOT = 0.514_444;
export const MAX_VESSEL_PROJECTION_MS = 90_000;
export const LARGE_CORRECTION_METERS = 250;
export const LARGE_CORRECTION_FADE_MS = 250;
export const IDLE_REPORTED_SPEED_KNOTS = 0.75;
export const IDLE_OBSERVED_SPEED_METERS_PER_SECOND = 0.2;
export const MAX_VESSEL_ESTIMATE_PATH_POINTS = 24;
const SPRING_OMEGA = 4.8;

export interface VesselMotionFrame {
  id: string;
  reported: RiverPoint;
  rendered: RiverPoint;
  /** Raw route tangent at the rendered point, following the route parametrisation. */
  tangent: RiverPoint;
  /** Unit vector the bow points along, already resolved for travel direction. */
  heading: RiverPoint;
  isEstimated: boolean;
  isIdle: boolean;
  renderedSMeters: number;
  renderedCorridorProgress: number;
  projectedSeconds: number;
  opacity: number;
  fadingFrom: { point: RiverPoint; opacity: number } | null;
}

interface MotionTrack {
  fix: PublicVesselSummary;
  /**
   * Signed along-route velocity derived from the two most recent reported
   * fixes. Undefined means this is still the first fix, so published speed is
   * the only bounded projection evidence available.
   */
  observedVelocityMetersPerSecond: number | undefined;
  /**
   * The bow direction currently shown. It only changes on physical evidence
   * (observed movement or a moving course), so a noisy direction flag cannot
   * spin the hull around between fixes.
   */
  facingSign: TravelSign | null;
  displaySMeters: number;
  displayProgress: number;
  velocitySMeters: number;
  velocityProgress: number;
  fadingFrom: RiverPoint | null;
  fadeStartedAtMs: number;
}

interface ProjectedFix {
  sMeters: number;
  corridorProgress: number;
  projectedSeconds: number;
}

export class VesselMotionController {
  private readonly tracks = new Map<string, MotionTrack>();
  private lastTickAtMs: number | null = null;

  update(vessels: readonly PublicVesselSummary[], nowMs: number): void {
    const incoming = new Set(vessels.map((vessel) => vessel.id));
    for (const id of this.tracks.keys()) {
      if (!incoming.has(id)) this.tracks.delete(id);
    }

    for (const vessel of vessels) {
      const existing = this.tracks.get(vessel.id);
      if (!existing) {
        this.tracks.set(vessel.id, {
          fix: vessel,
          observedVelocityMetersPerSecond: undefined,
          facingSign: resolveFacingSign(vessel, undefined, null),
          displaySMeters: vessel.sMeters,
          displayProgress: vessel.corridorProgress,
          velocitySMeters: 0,
          velocityProgress: 0,
          fadingFrom: null,
          fadeStartedAtMs: nowMs,
        });
        continue;
      }

      const previousObservedAtMs = Date.parse(existing.fix.observedAt);
      const nextObservedAtMs = Date.parse(vessel.observedAt);
      const sameObservation = existing.fix.observedAt === vessel.observedAt;
      const reportedRouteChanged =
        existing.fix.corridor !== vessel.corridor ||
        existing.fix.bridgePhase !== vessel.bridgePhase;
      const reportedPositionChanged =
        existing.fix.sMeters !== vessel.sMeters ||
        existing.fix.corridorProgress !== vessel.corridorProgress;
      if (sameObservation && !reportedRouteChanged && !reportedPositionChanged) {
        existing.fix = vessel;
        continue;
      }
      // A late network frame cannot become the basis of forward projection.
      if (
        Number.isFinite(previousObservedAtMs) &&
        Number.isFinite(nextObservedAtMs) &&
        nextObservedAtMs < previousObservedAtMs
      ) {
        continue;
      }

      const correction = Math.abs(existing.displaySMeters - vessel.sMeters);
      if (reportedRouteChanged || correction > LARGE_CORRECTION_METERS) {
        existing.fadingFrom = routeLocation(
          existing.fix.corridor,
          existing.displaySMeters,
          existing.displayProgress,
        ).point;
        existing.fadeStartedAtMs = nowMs;
        existing.displaySMeters = vessel.sMeters;
        existing.displayProgress = vessel.corridorProgress;
        existing.velocitySMeters = 0;
        existing.velocityProgress = 0;
      }
      if (!sameObservation) {
        existing.observedVelocityMetersPerSecond = observedRouteVelocity(
          existing.fix,
          vessel,
        );
      }
      existing.facingSign = resolveFacingSign(
        vessel,
        existing.observedVelocityMetersPerSecond,
        existing.facingSign,
      );
      existing.fix = vessel;
    }
  }

  tick(nowMs: number): VesselMotionFrame[] {
    const rawDeltaSeconds = this.lastTickAtMs === null ? 0 : (nowMs - this.lastTickAtMs) / 1_000;
    const deltaSeconds = Math.max(0, Math.min(0.1, rawDeltaSeconds));
    this.lastTickAtMs = nowMs;

    const frames: VesselMotionFrame[] = [];
    for (const [id, track] of this.tracks) {
      const projected = projectVesselFix(
        track.fix,
        nowMs,
        track.observedVelocityMetersPerSecond,
      );
      if (deltaSeconds > 0 && track.fadingFrom === null) {
        [track.displaySMeters, track.velocitySMeters] = criticallyDamped(
          track.displaySMeters,
          track.velocitySMeters,
          projected.sMeters,
          deltaSeconds,
        );
        [track.displayProgress, track.velocityProgress] = criticallyDamped(
          track.displayProgress,
          track.velocityProgress,
          projected.corridorProgress,
          deltaSeconds,
        );
      } else if (track.fadingFrom !== null) {
        track.displaySMeters = projected.sMeters;
        track.displayProgress = projected.corridorProgress;
      }

      const display = routeLocation(
        track.fix.corridor,
        track.displaySMeters,
        track.displayProgress,
      );
      const reported = routeLocation(
        track.fix.corridor,
        track.fix.sMeters,
        track.fix.corridorProgress,
      ).point;
      const fadeRatio = Math.min(
        1,
        Math.max(0, (nowMs - track.fadeStartedAtMs) / LARGE_CORRECTION_FADE_MS),
      );
      const fadingFrom = track.fadingFrom === null || fadeRatio >= 1
        ? null
        : { point: track.fadingFrom, opacity: 1 - fadeRatio };
      if (fadeRatio >= 1) track.fadingFrom = null;
      const idle = isIdleVessel(track.fix, track.observedVelocityMetersPerSecond);

      frames.push({
        id,
        reported,
        rendered: display.point,
        tangent: display.tangent,
        heading: vesselHeading(
          track.fix,
          display,
          track.displaySMeters,
          track.facingSign ?? 1,
          idle,
        ),
        isEstimated: projected.projectedSeconds > 0.01,
        isIdle: idle,
        renderedSMeters: track.displaySMeters,
        renderedCorridorProgress: track.displayProgress,
        projectedSeconds: projected.projectedSeconds,
        opacity: fadingFrom ? fadeRatio : 1,
        fadingFrom,
      });
    }
    return frames;
  }

  resume(nowMs: number): void {
    this.lastTickAtMs = nowMs;
    for (const track of this.tracks.values()) {
      track.velocitySMeters = 0;
      track.velocityProgress = 0;
    }
  }

  clear(): void {
    this.tracks.clear();
    this.lastTickAtMs = null;
  }
}

export function projectVesselFix(
  vessel: PublicVesselSummary,
  nowMs: number,
  observedVelocityMetersPerSecond?: number,
): ProjectedFix {
  const observedAtMs = Date.parse(vessel.observedAt);
  const sign = travelSign(vessel);
  if (
    !Number.isFinite(observedAtMs) ||
    sign === 0 ||
    isIdleVessel(vessel, observedVelocityMetersPerSecond) ||
    vessel.bridgePhase === "waiting" ||
    vessel.direction === "holding" ||
    vessel.direction === "unknown"
  ) {
    return fixedProjection(vessel);
  }

  const signedVelocity = observedVelocityMetersPerSecond === undefined
    ? publishedVelocity(vessel, sign)
    : observedVelocityMetersPerSecond;
  if (
    !Number.isFinite(signedVelocity) ||
    signedVelocity === 0 ||
    signedVelocity * sign <= 0
  ) return fixedProjection(vessel);

  const elapsedMs = Math.min(MAX_VESSEL_PROJECTION_MS, Math.max(0, nowMs - observedAtMs));
  const rawDistance = Math.abs(signedVelocity) * (elapsedMs / 1_000);
  const boundaryDistance = semanticBoundaryDistance(vessel, sign);
  const distance = softCap(rawDistance, boundaryDistance);
  if (distance <= 0) return fixedProjection(vessel);
  const sMeters = vessel.sMeters + sign * distance;
  return {
    sMeters,
    corridorProgress: projectedCorridorProgress(vessel, sMeters),
    projectedSeconds: elapsedMs / 1_000,
  };
}

export function sampleVesselEstimatePath(
  vessel: PublicVesselSummary,
  renderedSMeters: number,
  renderedCorridorProgress: number,
  maximumPoints = MAX_VESSEL_ESTIMATE_PATH_POINTS,
): RiverPoint[] {
  if (!Number.isInteger(maximumPoints) || maximumPoints < 2) {
    throw new RangeError("vessel estimate path requires at least two points");
  }
  const distanceMeters = Math.abs(renderedSMeters - vessel.sMeters);
  const progressDistance = Math.abs(renderedCorridorProgress - vessel.corridorProgress);
  const segmentCount = Math.min(
    maximumPoints - 1,
    Math.max(1, Math.ceil(distanceMeters / 35), Math.ceil(progressDistance * 18)),
  );
  return Array.from({ length: segmentCount + 1 }, (_, index) => {
    const ratio = index / segmentCount;
    return routeLocation(
      vessel.corridor,
      vessel.sMeters + (renderedSMeters - vessel.sMeters) * ratio,
      vessel.corridorProgress +
        (renderedCorridorProgress - vessel.corridorProgress) * ratio,
    ).point;
  });
}

/**
 * Orients the authored +X bow axis along the vessel's route travel using the
 * evidence available from a single fix. Live rendering uses the per-track
 * `heading` on each frame instead, which also carries facing hysteresis and
 * the open-bay course blend.
 */
export function vesselTravelTangent(
  tangent: RiverPoint,
  vessel: HeadingVessel | undefined,
  renderedSMeters: number,
): RiverPoint {
  if (!vessel) return tangent;
  const onBranch = vessel.corridor !== "miami_river" && renderedSMeters < RIVER_MOUTH_S_METERS;
  const along = onBranch ? { x: -tangent.x, z: -tangent.z } : tangent;
  const sign = vesselTravelSign(vessel, along);
  return sign < 0 ? { x: -along.x, z: -along.z } : along;
}

function fixedProjection(vessel: PublicVesselSummary): ProjectedFix {
  return {
    sMeters: vessel.sMeters,
    corridorProgress: vessel.corridorProgress,
    projectedSeconds: 0,
  };
}

function publishedVelocity(vessel: PublicVesselSummary, sign: -1 | 1): number {
  const speedKnots = vessel.speedKnots;
  return speedKnots !== null && Number.isFinite(speedKnots) && speedKnots > IDLE_REPORTED_SPEED_KNOTS
    ? sign * speedKnots * METERS_PER_SECOND_PER_KNOT
    : 0;
}

function isIdleVessel(
  vessel: PublicVesselSummary,
  observedVelocityMetersPerSecond: number | undefined,
): boolean {
  if (vessel.direction === "holding" || vessel.bridgePhase === "waiting") return true;
  const speedKnots = vessel.speedKnots;
  if (speedKnots !== null && Number.isFinite(speedKnots) && speedKnots <= IDLE_REPORTED_SPEED_KNOTS) {
    return true;
  }
  return observedVelocityMetersPerSecond !== undefined &&
    Math.abs(observedVelocityMetersPerSecond) <= IDLE_OBSERVED_SPEED_METERS_PER_SECOND &&
    (speedKnots === null || speedKnots <= 1.5);
}

function observedRouteVelocity(
  previous: PublicVesselSummary,
  next: PublicVesselSummary,
): number {
  const previousAtMs = Date.parse(previous.observedAt);
  const nextAtMs = Date.parse(next.observedAt);
  const elapsedSeconds = (nextAtMs - previousAtMs) / 1_000;
  if (!Number.isFinite(elapsedSeconds) || elapsedSeconds <= 0) return 0;
  const distance = next.sMeters - previous.sMeters;
  return Number.isFinite(distance) ? distance / elapsedSeconds : 0;
}

function semanticBoundaryDistance(vessel: PublicVesselSummary, sign: -1 | 1): number {
  const boundaries: number[] = [];
  const startsOnBranch = vessel.corridor !== "miami_river" && vessel.sMeters < RIVER_MOUTH_S_METERS;
  if (startsOnBranch) boundaries.push(RIVER_MOUTH_S_METERS);
  else if (vessel.sMeters > RIVER_MOUTH_S_METERS && sign < 0) boundaries.push(RIVER_MOUTH_S_METERS);

  if (
    (vessel.sMeters > 0 && sign < 0) ||
    (vessel.sMeters < 0 && vessel.sMeters >= RIVER_MOUTH_S_METERS && sign > 0)
  ) {
    boundaries.push(0);
  }

  if (vessel.bridgePhase === "waiting") return 0;
  const distances = boundaries
    .map((boundary) => (boundary - vessel.sMeters) / sign)
    .filter((distance) => distance >= 0);
  return distances.length === 0 ? Number.POSITIVE_INFINITY : Math.min(...distances);
}

function softCap(distance: number, cap: number): number {
  if (!Number.isFinite(cap)) return distance;
  if (cap <= 0) return 0;
  const easingStartsAt = cap * 0.72;
  if (distance <= easingStartsAt) return distance;
  const ratio = Math.min(1, (distance - easingStartsAt) / Math.max(0.001, cap - easingStartsAt));
  return easingStartsAt + (cap - easingStartsAt) * (1 - (1 - ratio) ** 3);
}

/**
 * Projection direction from the published fix alone. Observed movement is
 * deliberately excluded here: projection must never run against the
 * published movement, and the caller separately refuses to project when the
 * observed velocity opposes this sign.
 */
function travelSign(vessel: PublicVesselSummary): -1 | 0 | 1 {
  return vesselTravelSign(vessel, reportedAlongTangent(vessel));
}

function reportedAlongTangent(vessel: HeadingVessel): RiverPoint {
  return inboundRouteTangent(
    routeLocation(vessel.corridor, vessel.sMeters, vessel.corridorProgress),
  );
}

function resolveFacingSign(
  vessel: PublicVesselSummary,
  observedVelocityMetersPerSecond: number | undefined,
  previous: TravelSign | null,
): TravelSign | null {
  const sign = vesselTravelSign(vessel, reportedAlongTangent(vessel), {
    observedVelocityMetersPerSecond,
    previousSign: previous,
  });
  return sign === 0 ? previous : sign;
}

export function projectedCorridorProgress(vessel: PublicVesselSummary, sMeters: number): number {
  if (vessel.corridor === "miami_river" || vessel.sMeters >= RIVER_MOUTH_S_METERS) {
    return vessel.corridorProgress;
  }
  const observedDistance = RIVER_MOUTH_S_METERS - vessel.sMeters;
  if (observedDistance <= 0.001) return vessel.corridorProgress;
  const projectedDistance = Math.max(0, RIVER_MOUTH_S_METERS - sMeters);
  return Math.max(0, Math.min(1, vessel.corridorProgress * (projectedDistance / observedDistance)));
}

function criticallyDamped(
  value: number,
  velocity: number,
  target: number,
  deltaSeconds: number,
): [number, number] {
  const displacement = value - target;
  const impulse = velocity + SPRING_OMEGA * displacement;
  const decay = Math.exp(-SPRING_OMEGA * deltaSeconds);
  return [
    target + (displacement + impulse * deltaSeconds) * decay,
    (velocity - SPRING_OMEGA * impulse * deltaSeconds) * decay,
  ];
}
